// Angular
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// Material
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
// RxJS
import { skip } from 'rxjs/operators';
import {
  ContactRelationshipsService,
  PatientsService,
  selectContactRelationshipLatestSuccessfullAction,
  selectContactRelationshipsActionLoading,
} from 'src/app/core/lists';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../../../core/reducers';
import { GeneralAutocompleteComponent } from 'src/app/views/partials/layout/general-autocomplete/general-autocomplete.component';
import { StringUtilsService } from '../../../../../../../../core/services/string-utils.service';
import { ConfirmActionDialogComponent } from '../../../../../../../../views/partials/content/crud/';
import { ConfirmEntityDetailsDialogComponent } from './confirm-entity-details-dialog/confirm-entity-details-dialog';
import * as moment from 'moment';
import { AgreementTemplate } from 'src/app/core/agreement';
import { selectAllAgreementTemplates } from 'src/app/core/agreement/_selectors/agreement-template.selectors';
import { AllAgreementTemplatesRequested } from 'src/app/core/agreement/_actions/agreement-template.actions';
import { UserService } from 'src/app/core/auth/_services';
import { OfficeModel } from 'src/app/core/management';
import { ConfirmRevokeDialogComponent } from 'src/app/views/pages/agreement-management/agreement/agreement-list/confirm-revoke-dialog/confirm-revoke-dialog.component';
import { currentUser } from 'src/app/core/auth/_selectors/auth.selectors';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-relationship-edit-dialog',
  templateUrl: './relationship-edit-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RelationshipEditDialogComponent implements AfterViewInit, OnInit, OnDestroy {
  // Public properties
  relationshipEditForm: FormGroup;
  viewLoading = true;
  loadingAfterSubmit = false;
  filteredEntities: any[];
  entityType;
  category = '';
  relationCategoryList = [];
  relationCategoryDescriptionList = [];
  private subscriptions: any[] = [];
  entityAutocompleteCall;
  @ViewChild('entityAutocomplete') entityAutocomplete: GeneralAutocompleteComponent;
  entitySearchFields = [];
  minDate = moment(new Date()).format('YYYY-MM-DD');
  templates: AgreementTemplate[];
  offices: OfficeModel[];
  agreementData: any = undefined;
  userPrimaryOffice: number;

  /**
   * Component constructor
   *
   * @param dialogRef: MatDialogRef<PatientRelationshipEditComponent>
   * @param data: any
   * @param fb: FormBuilder
   * @param contactRelationshipsService: ContactRelationshipsService
   * @param cdr: ChangeDetectorRef
   */
  constructor(
    public dialogRef: MatDialogRef<RelationshipEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private contactRelationshipsService: ContactRelationshipsService,
    private patientService: PatientsService,
    private cdr: ChangeDetectorRef,
    public stringUtils: StringUtilsService,
    public dialog: MatDialog,
    private userService: UserService,
    private store: Store<AppState>,
  ) {}

  /**
   * On init
   */
  ngOnInit() {
    this.store.dispatch(new AllAgreementTemplatesRequested());

    this.subscriptions.push(
      this.store.pipe(select(selectAllAgreementTemplates)).subscribe((res) => {
        this.templates = res;
      }),
    );

    this.subscriptions.push(
      this.userService.getAllOffices().subscribe((res) => {
        this.offices = res;
      }),
    );

    // get the allowed offices for this user.
    this.store.pipe(select(currentUser)).subscribe((res) => {
      this.userPrimaryOffice = res.primaryOffice;
    });

    this.initRelationshipForm();
    this.entityType = this.relationshipEditForm.get('entity_type')?.value;
    this.entitySearchFields = this.stringUtils.getEntitySearchFields(this.entityType);
    if (this.data?.contact_id) {
      this.entityAutocompleteCall = this.contactRelationshipsService.searchActiveEntityByField.bind(
        this.contactRelationshipsService,
        this.entityType,
        this.data.contact_id,
        false,
      );
    }
    if (this.data?.contact_id && this.data?.fromAffiliation) {
      this.entityAutocompleteCall = this.contactRelationshipsService.searchActiveEntityByField.bind(
        this.contactRelationshipsService,
        this.entityType,
        this.data.contact_id,
        true,
      );
    }
    const loadingSub = this.store
      .pipe(select(selectContactRelationshipsActionLoading))
      .subscribe((res) => (this.viewLoading = res));
    this.subscriptions.push(loadingSub);
    this.loadCategory();
    const successSubscription = this.store
      .pipe(select(selectContactRelationshipLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          this.dialogRef.close();
        }
      });
    this.subscriptions.push(successSubscription);
    this.relationshipEditForm.get('relationCategory').valueChanges.subscribe((val) => {
      if (!val) return;
      this.category = val;
      this.contactRelationshipsService
        .getRelationTypeDescription(this.entityType, this.category)
        .subscribe((res) => {
          this.relationCategoryDescriptionList = res.data;
          if (this.data.item) {
            const category = {
              entity_type: this.data.item.entity_type,
              id: this.data.item.relation_category_id,
              name: this.data.item.relation_type,
            };
            const _description = {
              category,
              description_type: this.data.item.relationship_description,
              id: this.data.item.relation_type_id,
            };
            this.relationCategoryDescriptionList.push(_description);
            this.relationshipEditForm.get('relationCategoryDescription').setValue(_description);
          }
        });
    });

    this.relationshipEditForm.controls.financialResponsible.disable();

    this.viewLoading = false;
    this.cdr.detectChanges();
  }

  ngAfterViewInit(): void {
    if (this.data.isAffiliationTab) {
      this.relationshipEditForm.get('entity_type').setValue('AFFILIATION_GROUP');
      this.selectType('AFFILIATION_GROUP');
    }
    if (this.data.item && this.data.item.entity_id) {
      const entity = {
        first_name: this.data.item && this.data.item.first_name ? this.data.item.first_name : '',
        id: this.data.item.entity_id,
        last_name: this.data.item && this.data.item.last_name ? this.data.item.last_name : '',
      };
      this.relationshipEditForm
        .get('entityInput')
        .setValue(this.data.item && this.data.item.entity_id ? entity : null);
      this.entityAutocomplete.setValue(this.data.item && this.data.item.entity_id ? entity : null);
    }
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  /**
   * Form initalization
   * Default params, validators
   */
  initRelationshipForm() {
    this.relationshipEditForm = this.fb.group({
      entity_type: [
        this.data.item
          ? this.data.item.entity_type
          : this.data.isAffiliationTab
          ? 'AFFILIATION_GROUP'
          : 'PATIENT',
        [Validators.required],
      ],
      entityInput: [null, [Validators.required]],
      title: [this.data.item && this.data.item.title ? this.data.item.title : null],
      relationCategory: [null],
      relationCategoryDescription: [this.data.item ? this.data.item.relation_type_id : null],
      isPrimary: [this.data.item ? this.data.item.is_prime : false],
      financialResponsible: [
        {
          value: this.data.item ? this.data.item.financial_responsible : false,
          disabled: !this.data.item,
        },
      ],
      emergencyContact: [this.data.item ? this.data.item.emergency_contact : false],
      phiAuthorized: [this.data.item ? this.data.item.phi_authorized : false],
      validUntilRevoked: [
        this.data.item ? this.data.item.valid_until_revoked : null,
        Validators.required,
      ],
      authorizedDate: [
        this.data.item ? new Date(this.data.item.authorized_upto_date) : null,
        Validators.required,
      ],
    });
    if (!this.data.contactEntity) {
      this.relationshipEditForm.get('relationCategory').setValidators([Validators.required]);
      this.relationshipEditForm
        .get('relationCategory')
        .updateValueAndValidity({ emitEvent: false, onlySelf: true });
      this.relationshipEditForm
        .get('relationCategoryDescription')
        .setValidators([Validators.required]);
      this.relationshipEditForm.get('relationCategoryDescription').updateValueAndValidity({
        emitEvent: false,
        onlySelf: true,
      });
    }
    this.onPhiAuthorizedChange();
    this.onValidUntilRevokedChange();
  }

  selectType(entity_type) {
    this.entityType = entity_type;
    this.loadCategory();
    this.entitySearchFields = this.stringUtils.getEntitySearchFields(entity_type);
    this.entityAutocompleteCall = this.contactRelationshipsService.searchActiveEntityByField.bind(
      this.contactRelationshipsService,
      entity_type,
      this.data?.contact_id,
      false,
    );
    this.entityAutocomplete.setValue(null);
  }

  /**
   * Close dialog
   */
  onNoClick(): void {
    this.dialogRef.close({ isUpdated: false });
  }

  /**
   * Save relationship
   */
  save() {
    const controls = this.relationshipEditForm.controls;
    if (!this.data.contactEntity) {
      this.entityAutocomplete.updateValueAndValidity();
    }
    if (this.relationshipEditForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }

    let response;

    if (!this.data.contactEntity) {
      this.loadingAfterSubmit = true;
      this.viewLoading = true;
      const entity_id = controls.entityInput.value.id;
      const entity_type = controls.entity_type.value;
      const relationship_description = controls.relationCategoryDescription.value.description_type;
      const relation_type_id = controls.relationCategoryDescription.value.id;
      const relation_type = controls.relationCategory.value;
      const is_prime = controls.isPrimary.value;
      const title = controls.title.value;
      const financial_responsible = controls.financialResponsible.value;
      const emergency_contact = controls.emergencyContact.value;
      const phi_authorized = controls.phiAuthorized.value;
      const valid_until_revoked = controls.validUntilRevoked.value;
      const authorized_upto_date = controls.authorizedDate.value;
      this.viewLoading = false;
      response = {
        entity_id,
        entity_type,
        relation_type_id,
        relation_type,
        relationship_description,
        is_prime,
        title,
        phi_authorized,
        emergency_contact,
        financial_responsible,
        valid_until_revoked,
        authorized_upto_date,
      };
    } else {
      this.loadingAfterSubmit = true;
      this.viewLoading = true;
      const entity_id = controls.entityInput.value.id;
      const first_name = controls.entityInput.value.first_name;
      const middle_name = controls.entityInput.value.middle_name;
      const office_number = controls.entityInput.value.office_number;
      const extension = controls.entityInput.value.extension;
      const last_name = controls.entityInput.value.last_name;
      const last_name2 = controls.entityInput.value.last_name2;
      const email = controls.entityInput.value.email;
      const is_active = controls.entityInput.value.is_active;
      const cell_phone = controls.entityInput.value.cell_phone;
      const fax = controls.entityInput.value.fax;
      const state = controls.entityInput.value.state;
      const city = controls.entityInput.value.city;
      const zip = controls.entityInput.value.zip;
      const address1 = controls.entityInput.value.address1;
      const address2 = controls.entityInput.value.address2;
      const entity_type = controls.entity_type.value;
      const gender = controls.entityInput.value.gender ? controls.entityInput.value.gender : null;
      this.viewLoading = false;
      response = {
        entity_id,
        office_number,
        extension,
        first_name,
        middle_name,
        last_name,
        last_name2,
        email,
        is_active,
        cell_phone,
        fax,
        state,
        city,
        zip,
        address1,
        address2,
        entity_type,
        dob: controls.entityInput.value.dob,
        gender,
      };
    }

    response.agreement = this.agreementData ? this.agreementData : { action: 'none', data: {} };

    this.closeDialog(response);
  }

  /**
   * Close dialog
   *
   * @param specId: any
   */
  closeDialog(response) {
    this.dialogRef.close({
      done: true,
      response,
    });
  }

  /**
   * Checking control validation
   *
   * @param controlName: string => Equals to formControlName
   * @param validationType: string => Equals to valitors name
   */
  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.relationshipEditForm.controls[controlName];
    if (!control) {
      return false;
    }

    const result = control.hasError(validationType) && (control.dirty || control.touched);
    return result;
  }

  getTitle(): string {
    if (this.data.item) return 'CONTACT_RELATIONSHIP.EDIT_CONTACT_RELATIONSHIP';
    return 'CONTACT_RELATIONSHIP.NEW_CONTACT_RELATIONSHIP';
  }

  onEntityChange(val) {
    this.relationshipEditForm.controls.entityInput.setValue(val);
    this.relationshipEditForm.controls.entityInput.updateValueAndValidity();

    if (val !== null) {
      this.relationshipEditForm.controls.financialResponsible.enable();
    }

    if (this.data.item && this.data.item.relationship_description.trim().toUpperCase() == 'SELF') {
      this.relationshipEditForm.controls.financialResponsible.disable();
    }
  }

  loadCategory() {
    this.contactRelationshipsService.getRelationTypesCategory(this.entityType).subscribe((res) => {
      if (!res) return;
      this.relationCategoryList = res.data;
      if (this.data.item) {
        const category = {
          id: this.data.item.relation_category_id,
          entity_type: this.data.item.entity_type,
          name: this.data.item.relation_type,
        };
        this.relationCategoryList.push(category);
        this.relationshipEditForm.get('relationCategory').setValue(category);
      }
    });
  }

  getPrimaryString(controlName): string {
    if (this.relationshipEditForm.get(controlName).value) return 'Yes';
    return 'No';
  }

  onPhiAuthorizedChange() {
    if (this.relationshipEditForm.get('phiAuthorized').value === false) {
      this.relationshipEditForm.get('validUntilRevoked').setValue(null);
      this.relationshipEditForm.get('validUntilRevoked').disable();
      this.relationshipEditForm.get('validUntilRevoked').markAsUntouched();
      this.relationshipEditForm.get('validUntilRevoked').markAsPristine();

      this.relationshipEditForm.get('authorizedDate').setValue(null);
      this.relationshipEditForm.get('authorizedDate').disable();
      this.relationshipEditForm.get('authorizedDate').markAsUntouched();
      this.relationshipEditForm.get('authorizedDate').markAsPristine();
    } else {
      this.relationshipEditForm.get('validUntilRevoked').enable();
    }
  }
  onValidUntilRevokedChange() {
    if (this.relationshipEditForm.get('validUntilRevoked').value !== false) {
      this.relationshipEditForm.get('authorizedDate').disable();
      this.relationshipEditForm.get('authorizedDate').setValue(null);
      this.relationshipEditForm.get('authorizedDate').markAsUntouched();
      this.relationshipEditForm.get('authorizedDate').markAsPristine();
    } else {
      this.relationshipEditForm.get('authorizedDate').markAsUntouched();
      this.relationshipEditForm.get('authorizedDate').markAsPristine();
      this.relationshipEditForm.get('authorizedDate').enable();
    }
  }

  onFinanciallyResponsibleChange() {
    this.agreementData = undefined;

    if (!this.isFinanciallyResponsibleEnabled() && !this.isEdit()) {
      return;
    }

    // if the value is back to its initial state.
    if (
      this.data.item &&
      this.isFinanciallyResponsibleEnabled() == this.data.item.financial_responsible
    )
      return;

    let title = 'Disable Financially Responsible';
    let description =
      'If you disable financial responsibility, any active agreements with the entity will be rivoked.';

    if (this.isFinanciallyResponsibleEnabled()) {
      title = 'Enable Financially Responsible';
      description =
        'If you enable financial responsibility, an agreement document will be generated and sent to the entity for signing.';
    }

    description += ' Are you sure you wish to proceed?';

    const confirmActionDialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      data: {
        title: title,
        description: description,
        waitDesciption: 'Loading...',
      },
      width: '440px',
    });
    confirmActionDialogRef.afterClosed().subscribe((confirmed) => {
      const value = !!confirmed === this.isFinanciallyResponsibleEnabled();
      this.relationshipEditForm.get('financialResponsible').setValue(value);

      if (this.isFinanciallyResponsibleEnabled() && confirmed) {
        let observable;

        if (this.entityType === 'PATIENT') {
          observable = this.patientService.getPatientByIds([
            this.relationshipEditForm.controls.entityInput.value.id,
          ]);
        } else {
          observable = this.contactRelationshipsService.getEntity(
            this.relationshipEditForm.controls.entityInput.value.id,
          );
        }

        observable.subscribe((entity) => {
          const ref = this.dialog.open(ConfirmEntityDetailsDialogComponent, {
            data: {
              title: title,
              description: description,
              waitDesciption: 'Loading...',
              templates: this.templates,
              offices: this.offices,
              entity: Array.isArray(entity.data) ? entity.data[0] : entity.data,
              contact: this.data.contact,
              userPrimaryOffice: this.userPrimaryOffice,
            },
            height: '80vh',
            maxHeight: '80vh',
            width: '75vh',
            maxWidth: '75vh',
          });

          ref.afterClosed().subscribe((res) => {
            if (res) {
              this.agreementData = { action: 'create', data: res };
            } else {
              this.relationshipEditForm.get('financialResponsible').setValue(false);
            }
          });
        });
      } else if (!this.isFinanciallyResponsibleEnabled() && confirmed) {
        const dialogRef = this.dialog.open(ConfirmRevokeDialogComponent, {
          width: '450px',
          disableClose: true,
          closeOnNavigation: true,
        });

        dialogRef.afterClosed().subscribe((res) => {
          if (!res) {
            this.relationshipEditForm.get('financialResponsible').setValue(true);
          }

          this.agreementData = {
            action: 'revoke',
            data: {
              data: {
                reason: res.reason,
                entity_id: this.relationshipEditForm.controls.entityInput.value.id,
                contact_id: this.data.contact.id,
              },
            },
          };
        });
      }
    });
  }

  isFinanciallyResponsibleEnabled(): boolean {
    return this.relationshipEditForm.get('financialResponsible').value;
  }

  isEdit(): boolean {
    return this.data.item !== undefined;
  }

  agreementDataPresent(): boolean {
    return this.agreementData !== undefined;
  }
}
